/**
 * Validation utility functions for SalesGro.ai frontend
 * Implements validation rules based on backend model constraints
 */

/**
 * Team Validation Rules
 */
export const teamValidation = {
  /**
   * Validate team name
   * @param {string} name - Team name to validate
   * @param {Array} existingTeams - List of existing teams for uniqueness check
   * @param {string} currentTeamId - Current team ID (for edit scenarios)
   * @returns {string|null} - Error message or null if valid
   */
  validateName: (name, existingTeams = [], currentTeamId = null) => {
    if (!name || name.trim() === '') {
      return 'Team name is required';
    }
    
    // Check name uniqueness
    const nameExists = existingTeams.some(team => 
      team.id !== currentTeamId && 
      team.name.toLowerCase() === name.toLowerCase()
    );
    
    if (nameExists) {
      return 'Team name already exists';
    }
    
    return null;
  },
  
  /**
   * Validate parent team selection
   * @param {string} parentId - Selected parent team ID
   * @param {string} teamId - Current team ID
   * @param {Array} teams - List of all teams
   * @returns {string|null} - Error message or null if valid
   */
  validateParent: (parentId, teamId, teams) => {
    if (!parentId) return null; // Optional field
    
    // Can't be own parent
    if (parentId === teamId) {
      return 'A team cannot be its own parent';
    }
    
    // Check for circular references
    let currentParentId = parentId;
    const visited = new Set();
    
    while (currentParentId) {
      if (visited.has(currentParentId)) {
        return 'Circular team hierarchy detected';
      }
      
      visited.add(currentParentId);
      const parentTeam = teams.find(team => team.id === currentParentId);
      currentParentId = parentTeam ? parentTeam.parent_team : null;
      
      // If the parent chain leads back to the current team
      if (currentParentId === teamId) {
        return 'Circular team hierarchy detected';
      }
    }
    
    return null;
  },
  
  /**
   * Validate team lead
   * @param {string} leadId - Team lead user ID
   * @param {Array} users - List of all users
   * @returns {string|null} - Error message or null if valid
   */
  validateLead: (leadId, users) => {
    if (!leadId) return null; // Optional field
    
    const user = users.find(u => u.id === leadId);
    
    if (!user) {
      return 'Invalid team lead selected';
    }
    
    if (!['MG', 'AD'].includes(user.role)) {
      return 'Team lead must be a Manager or Admin';
    }
    
    return null;
  }
};

/**
 * User Profile Validation Rules
 */
export const userValidation = {
  /**
   * Validate user name
   * @param {string} name - User name to validate
   * @returns {string|null} - Error message or null if valid
   */
  validateName: (name) => {
    if (!name || name.trim() === '') {
      return 'Name is required';
    }
    
    return null;
  },
  
  /**
   * Validate user email
   * @param {string} email - User email to validate
   * @param {Array} existingUsers - List of existing users for uniqueness check
   * @param {string} currentUserId - Current user ID (for edit scenarios)
   * @returns {string|null} - Error message or null if valid
   */
  validateEmail: (email, existingUsers = [], currentUserId = null) => {
    if (!email || email.trim() === '') {
      return 'Email is required';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    
    // Check email uniqueness
    const emailExists = existingUsers.some(user => 
      user.id !== currentUserId && 
      user.email.toLowerCase() === email.toLowerCase()
    );
    
    if (emailExists) {
      return 'Email already in use';
    }
    
    return null;
  },
  
  /**
   * Validate mobile number
   * @param {string} mobile - Mobile number to validate
   * @returns {string|null} - Error message or null if valid
   */
  validateMobile: (mobile) => {
    if (!mobile || mobile.trim() === '') {
      return 'Mobile number is required';
    }
    
    if (mobile.length !== 10 || !/^\d{10}$/.test(mobile)) {
      return 'Mobile must be 10 digits';
    }
    
    return null;
  },
  
  /**
   * Validate user role
   * @param {string} role - User role to validate
   * @param {Array} existingUsers - List of existing users
   * @returns {string|null} - Error message or null if valid
   */
  validateRole: (role, existingUsers = []) => {
    if (!role) {
      return 'Role is required';
    }
    
    if (!['AD', 'MG', 'AG'].includes(role)) {
      return 'Invalid role';
    }
    
    // Only one admin per account is allowed
    if (role === 'AD' && existingUsers.some(user => user.role === 'AD')) {
      return 'An admin user already exists';
    }
    
    return null;
  },
  
  /**
   * Validate reporting structure
   * @param {string} reportsToId - Manager ID the user reports to
   * @param {string} userId - Current user ID
   * @param {Array} users - List of all users
   * @returns {string|null} - Error message or null if valid
   */
  validateReportsTo: (reportsToId, userId, users) => {
    if (!reportsToId) return null; // Optional field
    
    // Can't report to themselves
    if (reportsToId === userId) {
      return 'User cannot report to themselves';
    }
    
    const manager = users.find(u => u.id === reportsToId);
    
    if (!manager) {
      return 'Invalid manager selected';
    }
    
    if (!['MG', 'AD'].includes(manager.role)) {
      return 'Users can only report to Managers or Admins';
    }
    
    // Check for circular reporting structure
    let currentManagerId = reportsToId;
    const visited = new Set();
    
    while (currentManagerId) {
      if (visited.has(currentManagerId)) {
        return 'Circular reporting structure detected';
      }
      
      visited.add(currentManagerId);
      const managerUser = users.find(user => user.id === currentManagerId);
      currentManagerId = managerUser ? managerUser.reports_to : null;
      
      // If the manager chain leads back to the current user
      if (currentManagerId === userId) {
        return 'Circular reporting structure detected';
      }
    }
    
    return null;
  },
  
  /**
   * Validate employee ID
   * @param {string} employeeId - Employee ID to validate
   * @param {Array} existingUsers - List of existing users for uniqueness check
   * @param {string} currentUserId - Current user ID (for edit scenarios)
   * @returns {string|null} - Error message or null if valid
   */
  validateEmployeeId: (employeeId, existingUsers = [], currentUserId = null) => {
    if (!employeeId) return null; // Optional field
    
    // Check employee ID uniqueness within the account
    const idExists = existingUsers.some(user => 
      user.id !== currentUserId && 
      user.employee_id === employeeId
    );
    
    if (idExists) {
      return 'Employee ID already in use';
    }
    
    return null;
  }
};

/**
 * Virtual Number Validation Rules
 */
export const virtualNumberValidation = {
  /**
   * Validate phone number
   * @param {string} number - Phone number to validate
   * @returns {string|null} - Error message or null if valid
   */
  validatePhoneNumber: (number) => {
    if (!number || number.trim() === '') {
      return 'Phone number is required';
    }
    
    // Simple validation for now - can be enhanced based on requirements
    if (!/^\d{10,15}$/.test(number)) {
      return 'Phone number must be 10-15 digits';
    }
    
    return null;
  },
  
  /**
   * Validate vendor selection
   * @param {string} vendor - Selected vendor code
   * @returns {string|null} - Error message or null if valid
   */
  validateVendor: (vendor) => {
    if (!vendor) {
      return 'Vendor is required';
    }
    
    if (!['KOM', 'TLS'].includes(vendor)) {
      return 'Invalid vendor';
    }
    
    return null;
  }
};

/**
 * Complete team validation
 * @param {Object} teamData - Full team data object
 * @param {Array} existingTeams - List of existing teams
 * @param {Array} users - List of all users
 * @param {string} currentTeamId - Current team ID (for edit scenarios)
 * @returns {Object} - Object with validation errors, empty if valid
 */
export const validateTeam = (teamData, existingTeams = [], users = [], currentTeamId = null) => {
  const errors = {};
  
  const nameError = teamValidation.validateName(teamData.name, existingTeams, currentTeamId);
  if (nameError) errors.name = nameError;
  
  const parentError = teamValidation.validateParent(teamData.parent_team, currentTeamId, existingTeams);
  if (parentError) errors.parent_team = parentError;
  
  const leadError = teamValidation.validateLead(teamData.lead, users);
  if (leadError) errors.lead = leadError;
  
  return errors;
};

/**
 * Complete user validation
 * @param {Object} userData - Full user data object
 * @param {Array} existingUsers - List of existing users
 * @param {string} currentUserId - Current user ID (for edit scenarios)
 * @returns {Object} - Object with validation errors, empty if valid
 */
export const validateUser = (userData, existingUsers = [], currentUserId = null) => {
  const errors = {};
  
  const nameError = userValidation.validateName(userData.name);
  if (nameError) errors.name = nameError;
  
  const emailError = userValidation.validateEmail(userData.email, existingUsers, currentUserId);
  if (emailError) errors.email = emailError;
  
  const mobileError = userValidation.validateMobile(userData.mobile);
  if (mobileError) errors.mobile = mobileError;
  
  const roleError = userValidation.validateRole(userData.role, existingUsers);
  if (roleError) errors.role = roleError;
  
  const reportsToError = userValidation.validateReportsTo(userData.reports_to, currentUserId, existingUsers);
  if (reportsToError) errors.reports_to = reportsToError;
  
  const employeeIdError = userValidation.validateEmployeeId(userData.employee_id, existingUsers, currentUserId);
  if (employeeIdError) errors.employee_id = employeeIdError;
  
  return errors;
};
